import { getApiBaseUrl } from '../config/api.config'
import { getTokenFromUrl } from '../utils/auth'
import { configureDebugLogging, debug } from '../utils/debug'

import type { EnvironmentWithChina } from '../App'
import type { CustomerData, UpdateCustomerPayload } from '../types/customer'

export class CustomerApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: unknown,
  ) {
    super(message)
    this.name = 'CustomerApiError'
  }
}

interface FetchCustomerDataParams {
  accountNumber?: string
  customerProfileEmail?: string
}

function normalizeIdentifier(value?: string): string {
  if (!value) return ''

  try {
    return decodeURIComponent(value).trim()
  } catch {
    return value.trim()
  }
}

function isCustomerData(data: unknown): data is CustomerData {
  if (!data || typeof data !== 'object') {
    return false
  }

  const record = data as Record<string, unknown>
  return (
    typeof record.accountNumber === 'string' &&
    'email' in record &&
    'firstName' in record &&
    'lastName' in record
  )
}

function resolveEmailLookupResponse(
  data: unknown,
  customerProfileEmail: string,
): CustomerData {
  if (isCustomerData(data)) {
    return data
  }

  if (Array.isArray(data)) {
    const normalizedEmail = customerProfileEmail.toLowerCase()
    const customers = data.filter(isCustomerData)
    const exactMatch = customers.find(
      (customer) => customer.email?.toLowerCase() === normalizedEmail,
    )

    if (exactMatch) {
      return exactMatch
    }

    if (customers.length === 1) {
      return customers[0]
    }
  }

  throw new CustomerApiError(
    `Unexpected email lookup response for: ${customerProfileEmail}`,
    500,
    data,
  )
}

export async function fetchCustomerData(
  params: FetchCustomerDataParams,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<CustomerData> {
  configureDebugLogging(env)

  const accountNumber = normalizeIdentifier(params.accountNumber)
  const customerProfileEmail = normalizeIdentifier(params.customerProfileEmail)
  const baseUrl = getApiBaseUrl(env)
  const isEmailLookup = !!customerProfileEmail
  const url = isEmailLookup
    ? `${baseUrl}/public/customerapi/Accounts?searchText=${encodeURIComponent(customerProfileEmail)}`
    : `${baseUrl}/public/customerapi/Accounts/${encodeURIComponent(accountNumber)}`

  if (!isEmailLookup && !accountNumber) {
    throw new CustomerApiError(
      'Missing customer identifier. Provide accountNumber or customerProfileEmail.',
    )
  }

  // Priority: passed authToken > URL token
  const token = authToken || getTokenFromUrl()

  debug.log('[CustomerAPI] Request details:', {
    url,
    accountNumber,
    customerProfileEmail,
    lookupMode: isEmailLookup ? 'email' : 'accountNumber',
    env,
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 20)}...` : null,
  })

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Add Authorization header if token is present
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
    debug.log('[CustomerAPI] Authorization header added')
  } else {
    debug.warn(
      '[CustomerAPI] No token found in URL. Add ?token=YOUR_TOKEN to the URL',
    )
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'omit',
    })

    debug.log('[CustomerAPI] Response status:', response.status)

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new CustomerApiError(
        `Failed to fetch customer data: ${response.statusText}`,
        response.status,
        errorText,
      )
    }

    const data = await response.json()
    if (isEmailLookup) {
      return resolveEmailLookupResponse(data, customerProfileEmail)
    }

    return data as CustomerData
  } catch (error) {
    if (error instanceof CustomerApiError) {
      throw error
    }

    throw new CustomerApiError(
      `Network error while fetching customer data: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }
}

export async function updateCustomer(
  accountNumber: string,
  payload: UpdateCustomerPayload,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<CustomerData> {
  configureDebugLogging(env)

  const baseUrl = getApiBaseUrl(env)
  const url = `${baseUrl}/public/customerapi/Accounts/${accountNumber}`

  // Priority: passed authToken > URL token
  const token = authToken || getTokenFromUrl()

  debug.log('[CustomerAPI] Update customer request:', {
    url,
    accountNumber,
    env,
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 20)}...` : null,
    payload,
  })

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Add Authorization header if token is present
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
    debug.log('[CustomerAPI] Authorization header added')
  } else {
    debug.warn(
      '[CustomerAPI] No token found in URL. Add ?token=YOUR_TOKEN to the URL',
    )
  }

  debug.log('[CustomerAPI] Request headers:', headers)

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify(payload),
    })

    debug.log('[CustomerAPI] Response status:', response.status)

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      throw new CustomerApiError(
        `Failed to update customer data: ${response.statusText}`,
        response.status,
        errorText,
      )
    }

    const data = await response.json()
    return data as CustomerData
  } catch (error) {
    if (error instanceof CustomerApiError) {
      throw error
    }

    throw new CustomerApiError(
      `Network error while updating customer data: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }
}
